import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { PACK_STATUS_LABELS } from '@/lib/pack-readiness'

interface StatusOption {
  value: string
  label: string
  variant: BadgeProps['variant']
  dotColor: string
}

interface StatusDropdownProps {
  value: string
  options: StatusOption[]
  onChange: (value: string) => void
  disabled?: boolean | undefined
}

// Preset status configs — using Badge variants from the design system
export const COMPLIANCE_STATUS_OPTIONS: StatusOption[] = [
  { value: 'pending', label: 'Not started', variant: 'default', dotColor: 'bg-muted-foreground' },
  { value: 'in_progress', label: 'In progress', variant: 'warning', dotColor: 'bg-[hsl(var(--status-warning))]' },
  { value: 'completed', label: 'Done', variant: 'success', dotColor: 'bg-[hsl(var(--status-success))]' },
  { value: 'not_applicable', label: 'No action', variant: 'secondary', dotColor: 'bg-muted-foreground' },
]

export const RISK_STATUS_OPTIONS: StatusOption[] = [
  { value: 'identified', label: 'Identified', variant: 'default', dotColor: 'bg-muted-foreground' },
  { value: 'assessing', label: 'Assessing', variant: 'warning', dotColor: 'bg-[hsl(var(--status-warning))]' },
  { value: 'mitigated', label: 'Mitigated', variant: 'success', dotColor: 'bg-[hsl(var(--status-success))]' },
  { value: 'accepted', label: 'Accepted', variant: 'secondary', dotColor: 'bg-muted-foreground' },
  { value: 'escalated', label: 'Escalated', variant: 'destructive', dotColor: 'bg-[hsl(var(--status-error))]' },
]

export const SUBMISSION_STATUS_OPTIONS: StatusOption[] = [
  { value: 'not_ready', label: PACK_STATUS_LABELS.not_ready, variant: 'default', dotColor: 'bg-muted-foreground' },
  { value: 'in_progress', label: PACK_STATUS_LABELS.in_progress, variant: 'warning', dotColor: 'bg-[hsl(var(--status-warning))]' },
  { value: 'ready', label: PACK_STATUS_LABELS.ready, variant: 'success', dotColor: 'bg-[hsl(var(--status-success))]' },
  { value: 'not_required', label: PACK_STATUS_LABELS.not_required, variant: 'secondary', dotColor: 'bg-muted-foreground' },
]

export function StatusDropdown({ value, options, onChange, disabled }: StatusDropdownProps) {
  const current = options.find((o) => o.value === value) ?? options[0]

  if (!current) return null

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled ?? false}>
      <SelectTrigger className="h-7 w-auto text-xs border-0 bg-transparent hover:bg-accent/50 transition-colors gap-1.5 px-2">
        <SelectValue>
          <Badge variant={current.variant} dot className="text-xs font-normal whitespace-nowrap">
            {current.label}
          </Badge>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            <span className="flex items-center gap-2 text-xs">
              <span className={`h-2 w-2 rounded-full ${option.dotColor}`} />
              {option.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
